/**
 * Firebase — platform-specific initialization for the Vite web app.
 *
 * db uses persistentLocalCache (IndexedDB) + persistentMultipleTabManager
 * so the web app works offline and across browser tabs.
 *
 * db/storage/functions are NOT in @cockpit/firebase because those APIs
 * use browser-only storage backends that crash React Native.
 *
 * All imports in this app use '@shared/api' via vite-tsconfig-paths.
 */
import { app } from '@cockpit/firebase';

import { getAuth, OAuthProvider, signInWithPopup, connectAuthEmulator } from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
} from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

export const auth = getAuth(app);

// Microsoft SSO — web only. Pinned to our own Entra tenant (Einfach Nachhilfe
// GmbH) instead of `common`: the sign-in app registration is single-tenant,
// and Microsoft rejects a single-tenant app on the `common` endpoint
// (AADSTS50194).
const MICROSOFT_TENANT_ID = 'd8c9b2a2-f868-43af-ad95-286eed0fd648';
const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.setCustomParameters({ tenant: MICROSOFT_TENANT_ID });
export const signInWithMicrosoft = () => signInWithPopup(auth, microsoftProvider);
export const signOut = () => auth.signOut();

// IndexedDB persistence — web only (multi-tab aware)
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});

export const storage = getStorage(app);
export const functions = getFunctions(app, 'europe-west3');

const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';
if (useEmulators) {
  // Reach the emulators on whichever host served this page, not on the browser's
  // own machine: the dev server runs on the VPS while the browser sits on a
  // laptop, so a hardcoded `localhost` would look for the emulators there.
  const host = window.location.hostname;
  // Ports are overridable so a second checkout can run its own emulator set
  // side by side. `import.meta.env` keys must be referenced statically — Vite
  // replaces them at build time and a computed lookup would silently be
  // undefined. Falling back to the standard ports keeps the shipped build
  // behaving exactly as before when nothing is set.
  const port = (value: string | undefined, fallback: number): number => Number(value) || fallback;
  const authPort = port(import.meta.env.VITE_EMULATOR_AUTH_PORT, 9099);
  const firestorePort = port(import.meta.env.VITE_EMULATOR_FIRESTORE_PORT, 8080);
  const storagePort = port(import.meta.env.VITE_EMULATOR_STORAGE_PORT, 9199);
  const functionsPort = port(import.meta.env.VITE_EMULATOR_FUNCTIONS_PORT, 5001);

  connectAuthEmulator(auth, `http://${host}:${authPort}`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, firestorePort);
  connectStorageEmulator(storage, host, storagePort);
  connectFunctionsEmulator(functions, host, functionsPort);
  if (import.meta.env.DEV) console.log(`[Firebase] Using emulators on ${host}`);
} else {
  // Started with `--mode emulators` but the flag is missing: the app would
  // silently write to live data instead. That is worth shouting about — the
  // flag comes from apps/web/.env.emulators, which is gitignored and therefore
  // absent on a fresh checkout.
  if (import.meta.env.MODE === 'emulators') {
    console.error(
      '[Firebase] Mode is "emulators" but VITE_USE_EMULATORS is not "true" — ' +
        'connecting to PRODUCTION. Create apps/web/.env.emulators containing ' +
        'VITE_USE_EMULATORS=true (see .env.emulators.example) and restart.'
    );
  }
  if (import.meta.env.DEV) console.log('[Firebase] Using production');
}

export { app };
